/**
 * PromptSettingsTab - Admin UI for managing MCP AI instructions
 * 
 * Matches the design of the Test Harness tab - Card-based collapsible sections
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { 
  Save, Loader2, ChevronDown, ChevronUp, 
  ListChecks, Brain, Activity, FileText, GitCommit, 
  RefreshCw, Sparkles, Code, Server, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// All prompt configurations - endpoints and work item types together
const PROMPT_CONFIGS = [
  // Read Endpoints
  {
    id: 'features_ready',
    type: 'endpoint',
    category: 'read',
    name: 'List Features Ready',
    path: '/api/v1/mcp/features/ready',
    method: 'GET',
    description: 'Injected at top of response',
    icon: ListChecks,
  },
  {
    id: 'context',
    type: 'endpoint',
    category: 'read',
    name: 'Get MCP Context',
    path: '/api/v1/mcp/context',
    method: 'GET',
    description: 'Injected at top of response',
    icon: Brain,
  },
  {
    id: 'activity',
    type: 'endpoint',
    category: 'read',
    name: 'Activity Feed',
    path: '/api/v1/mcp/activity',
    method: 'GET',
    description: 'Injected at top of response',
    icon: Activity,
  },
  // Write Endpoints
  {
    id: 'log_decision',
    type: 'endpoint',
    category: 'write',
    name: 'Log Technical Decision',
    path: '/api/v1/mcp/features/{id}/log',
    method: 'POST',
    description: 'Next action after success',
    icon: FileText,
  },
  {
    id: 'change_status',
    type: 'endpoint',
    category: 'write',
    name: 'Change Status',
    path: '/api/v1/mcp/features/{id}/status',
    method: 'PATCH',
    description: 'Next action after success',
    icon: RefreshCw,
  },
  {
    id: 'link_commit',
    type: 'endpoint',
    category: 'write',
    name: 'Link Commit',
    path: '/api/v1/mcp/commits/link',
    method: 'POST',
    description: 'Next action after success',
    icon: GitCommit,
  },
  // Work Item Types
  {
    id: 'feature',
    type: 'work_item',
    category: 'work_item',
    name: 'Feature Work Item',
    path: 'current_feature (type: feature)',
    method: 'CONTEXT',
    description: 'Injected into current_feature',
    icon: Code,
  },
  {
    id: 'infrastructure',
    type: 'work_item',
    category: 'work_item',
    name: 'Infrastructure Build',
    path: 'current_feature (type: infrastructure)',
    method: 'CONTEXT',
    description: 'Injected into current_feature',
    icon: Server,
  },
];

// Variable chip component
const VariableChip = ({ variable, onInsert }) => (
  <button
    onClick={() => onInsert(variable.var)}
    className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-500/10 text-blue-600 dark:text-blue-400 rounded-md hover:bg-blue-500/20 transition-colors border border-blue-500/20"
    title={variable.desc}
  >
    <Code className="h-3 w-3" />
    {variable.var}
  </button>
);

// Single Prompt Card Component
const PromptCard = ({
  config,
  instruction,
  variables,
  expanded,
  onToggle,
  onSave,
  saving
}) => {
  const [value, setValue] = useState(instruction || '');
  const [showPreview, setShowPreview] = useState(false);
  const textareaRef = React.useRef(null);
  const Icon = config.icon;

  // Sync with external instruction changes
  useEffect(() => {
    if (instruction !== value) {
      setValue(instruction || '');
    }
  }, [instruction, value]);

  const insertVariable = useCallback((varName) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newValue = value.substring(0, start) + varName + value.substring(end);
    setValue(newValue);
    
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + varName.length, start + varName.length);
    }, 0);
  }, [value]);

  const hasChanges = value !== (instruction || '');

  // Generate preview with sample values
  const preview = value
    .replace(/\{\{product_name\}\}/g, 'Widget Dashboard')
    .replace(/\{\{feature_count\}\}/g, '5')
    .replace(/\{\{has_blocking_build\}\}/g, 'true')
    .replace(/\{\{depth\}\}/g, 'standard')
    .replace(/\{\{activity_count\}\}/g, '25')
    .replace(/\{\{feature_id\}\}/g, 'feat_abc123')
    .replace(/\{\{feature_title\}\}/g, 'User Authentication')
    .replace(/\{\{feature_status\}\}/g, 'In Progress')
    .replace(/\{\{build_id\}\}/g, 'infra_bootstrap_123')
    .replace(/\{\{cloud_provider\}\}/g, 'aws')
    .replace(/\{\{compute_type\}\}/g, 'lambda')
    .replace(/\{\{services_list\}\}/g, 'dynamodb, s3, cognito');

  const getMethodBadgeVariant = () => {
    if (config.method === 'GET') return 'secondary';
    if (config.method === 'CONTEXT') return 'outline';
    return 'default';
  };

  return (
    <Card className={cn(
      "transition-all",
      expanded && "ring-2 ring-blue-500/20"
    )}>
      <Collapsible open={expanded} onOpenChange={onToggle}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Icon className="w-5 h-5 text-gray-500" />
                <div>
                  <div className="flex items-center gap-2">
                    <Badge 
                      variant={getMethodBadgeVariant()}
                      className="text-xs"
                    >
                      {config.method}
                    </Badge>
                    <span className="font-medium">{config.name}</span>
                    {instruction && (
                      <Badge variant="outline" className="text-xs text-green-600 border-green-300">
                        Configured
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5 font-mono">{config.path}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <CardContent className="pt-0 space-y-4">
            <p className="text-sm text-gray-500">{config.description}</p>
            
            {/* Variable chips */}
            {variables && variables.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {variables.map((v) => (
                  <VariableChip key={v.var} variable={v} onInsert={insertVariable} />
                ))}
              </div>
            )}
            
            {/* Textarea */}
            <Textarea
              ref={textareaRef}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="Enter AI instructions... Use {{variables}} for dynamic values"
              className="min-h-[100px] font-mono text-sm"
            />
            
            {/* Preview toggle */}
            {value && (
              <div className="space-y-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPreview(!showPreview)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  {showPreview ? 'Hide Preview' : 'Show Preview'}
                </Button>
                
                {showPreview && (
                  <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border">
                    <p className="text-xs text-gray-500 mb-2">Preview (with sample values):</p>
                    <p className="text-sm whitespace-pre-wrap">{preview}</p>
                  </div>
                )}
              </div>
            )}
            
            {/* Save button */}
            <div className="flex items-center gap-3">
              <Button
                onClick={() => onSave(config.id, config.type, value)}
                disabled={saving || !hasChanges}
                size="sm"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save
              </Button>
              {hasChanges && (
                <span className="text-xs text-amber-600">Unsaved changes</span>
              )}
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

export default function PromptSettingsTab() {
  const [settings, setSettings] = useState(null);
  const [variables, setVariables] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [expandedItems, setExpandedItems] = useState(['features_ready']);

  // Fetch settings and variables
  useEffect(() => {
    const fetchData = async () => {
      // Small delay to ensure session is set after navigation
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const token = localStorage.getItem('session_token');
      if (!token) {
        // Retry once after a short delay
        await new Promise(resolve => setTimeout(resolve, 500));
        const retryToken = localStorage.getItem('session_token');
        if (!retryToken) {
          setLoading(false);
          return;
        }
      }
      
      const authToken = localStorage.getItem('session_token');
      
      try {
        const [settingsRes, variablesRes] = await Promise.all([
          fetch(`${API_BASE}/api/admin/mcp-prompt-settings`, {
            headers: { 'Authorization': `Bearer ${authToken}` }
          }),
          fetch(`${API_BASE}/api/admin/mcp-prompt-settings/variables`, {
            headers: { 'Authorization': `Bearer ${authToken}` }
          })
        ]);
        
        if (settingsRes.status === 401 || variablesRes.status === 401) {
          setLoading(false);
          return;
        }
        
        if (!settingsRes.ok || !variablesRes.ok) {
          throw new Error('Failed to fetch settings');
        }
        
        const settingsData = await settingsRes.json();
        const variablesData = await variablesRes.json();
        
        setSettings(settingsData);
        setVariables(variablesData);
      } catch (error) {
        console.error('Error fetching prompt settings:', error);
        // Don't show toast for auth errors on initial load
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Get instruction for a config
  const getInstruction = (config) => {
    if (!settings) return '';
    if (config.type === 'endpoint') {
      return settings.endpoint_instructions?.[config.id] || '';
    } else {
      return settings.work_item_instructions?.[config.id] || '';
    }
  };

  // Get variables for a config
  const getVariables = (config) => {
    if (!variables) return [];
    if (config.type === 'endpoint') {
      return variables.endpoint_variables?.[config.id] || [];
    } else {
      return variables.work_item_variables?.[config.id] || [];
    }
  };

  // Save instruction
  const saveInstruction = async (id, type, instruction) => {
    setSaving(id);
    try {
      const token = localStorage.getItem('session_token');
      const endpoint = type === 'endpoint' 
        ? '/api/admin/mcp-prompt-settings/endpoint'
        : '/api/admin/mcp-prompt-settings/work-item';
      
      const body = type === 'endpoint'
        ? { endpoint: id, instructions: instruction }
        : { work_item_type: id, instructions: instruction };
      
      const res = await fetch(`${API_BASE}${endpoint}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      
      if (!res.ok) throw new Error('Failed to save');
      
      // Update local state
      if (type === 'endpoint') {
        setSettings(prev => ({
          ...prev,
          endpoint_instructions: {
            ...prev.endpoint_instructions,
            [id]: instruction
          }
        }));
      } else {
        setSettings(prev => ({
          ...prev,
          work_item_instructions: {
            ...prev.work_item_instructions,
            [id]: instruction
          }
        }));
      }
      
      toast.success('Saved successfully');
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save instructions');
    } finally {
      setSaving(null);
    }
  };

  const toggleItem = (id) => {
    setExpandedItems(prev => 
      prev.includes(id) ? prev.filter(e => e !== id) : [...prev, id]
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const readEndpoints = PROMPT_CONFIGS.filter(c => c.category === 'read');
  const writeEndpoints = PROMPT_CONFIGS.filter(c => c.category === 'write');
  const workItemTypes = PROMPT_CONFIGS.filter(c => c.category === 'work_item');

  return (
    <div className="space-y-6">
      {/* Description Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            AI Prompt Settings
          </CardTitle>
          <CardDescription>
            Configure instructions that are injected into MCP API responses. Use {"{{variables}}"} for dynamic values.
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Read Endpoints */}
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Eye className="w-4 h-4" />
          Read Operations
        </div>
        {readEndpoints.map(config => (
          <PromptCard
            key={config.id}
            config={config}
            instruction={getInstruction(config)}
            variables={getVariables(config)}
            expanded={expandedItems.includes(config.id)}
            onToggle={() => toggleItem(config.id)}
            onSave={saveInstruction}
            saving={saving === config.id}
          />
        ))}
      </div>

      {/* Write Endpoints */}
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Code className="w-4 h-4" />
          Write Operations
        </div>
        {writeEndpoints.map(config => (
          <PromptCard
            key={config.id}
            config={config}
            instruction={getInstruction(config)}
            variables={getVariables(config)}
            expanded={expandedItems.includes(config.id)}
            onToggle={() => toggleItem(config.id)}
            onSave={saveInstruction}
            saving={saving === config.id}
          />
        ))}
      </div>

      {/* Work Item Types */}
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Server className="w-4 h-4" />
          Work Item Types (injected into current_feature)
        </div>
        {workItemTypes.map(config => (
          <PromptCard
            key={config.id}
            config={config}
            instruction={getInstruction(config)}
            variables={getVariables(config)}
            expanded={expandedItems.includes(config.id)}
            onToggle={() => toggleItem(config.id)}
            onSave={saveInstruction}
            saving={saving === config.id}
          />
        ))}
      </div>
    </div>
  );
}
